import 'whatwg-fetch'
import { debug } from '../debug'
import { CallOptions, LoadOptions, SubmitOptions, WithBodyOptions, WithoutBodyOptions } from './types'

// Preferably use the `useJsonApi` webhook.
// Otherwise use `.call` for Promise/await based fetching.
// The other methods require a `success` callback.
export class JSONFetcher {
  static load<Out>({ url, success, signal }: LoadOptions<Out>) {
    fetch(url, { signal: signal })
      .then((response) => response.json())
      .then((json) => success(json))
      .then(undefined, (err) => debug(err))
  }

  static post<In, Out>({ ...opts }: WithBodyOptions<In, Out>) {
    this.submit({ method: 'post', ...opts })
  }

  static put<In, Out>({ ...opts }: WithBodyOptions<In, Out>) {
    this.submit({ method: 'put', ...opts })
  }

  static delete({ ...opts }: WithoutBodyOptions<never>) {
    this.submit({ method: 'delete', ...opts })
  }

  static async call<In, Out>({ ...opts }: CallOptions<In, Out>): Promise<Out> {
    return new Promise((resolve, reject) => {
      this.submit({ ...opts, success: resolve, onError: reject })
    })
  }

  static submit<In, Out>({ url, body, success, method = 'get', onError, signal }: SubmitOptions<In, Out>) {
    fetch(url, {
      method,
      signal,
      body: JSON.stringify(body),
      credentials: 'same-origin',
      headers: JSONFetcher.buildHeaders(),
    })
      .then(this.checkStatus)
      .then((response) => response.json())
      .then((json) => success(json))
      .then(undefined, (err) => {
        debug(err)
        onError && onError(err)
      })
  }

  static buildHeaders(): HeadersInit {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
    return new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': token,
    })
  }

  static async checkStatus(response: Response): Promise<Response> {
    if (response.ok || response.status === 409) return response
    else throw new Error(`JSONFetcher: ${response.status} ${response.statusText}`)
  }
}
